import type { ThreadPullRequestKey } from "@afar-research/contracts";

/**
 * Reads a pull request's membership identity out of its web address. The address is what the
 * host itself handed back, so it identifies the host and repository even when the checkout's
 * remote points somewhere else — at a fork, most commonly, whose pull request lives on the
 * upstream repository. AFAR reads GitHub, so anything that is not a GitHub pull request page
 * is refused rather than guessed at.
 */
export function parsePullRequestUrl(url: string): ThreadPullRequestKey | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
  const segments = parsed.pathname.split("/").filter((segment) => segment.length > 0);
  // owner / repository / "pull" / number, and nothing shorter.
  if (segments.length < 4) return null;
  const [owner, repository, kind, rawNumber] = segments;
  if (owner === undefined || repository === undefined || kind !== "pull") return null;
  if (rawNumber === undefined || !/^\d+$/u.test(rawNumber)) return null;
  const number = Number.parseInt(rawNumber, 10);
  if (number <= 0) return null;
  const host = parsed.hostname.trim().toLowerCase();
  if (host.length === 0) return null;
  return { host, repository: `${owner}/${repository}`.toLowerCase(), number };
}

/**
 * The repository an AFAR Project points at, as a pull request target. `canonicalKey` is
 * `host/owner/name`. Null for a Project with no recognised GitHub remote, which is what makes a
 * bare number unresolvable and a host-defaulted target unreadable: AFAR reads GitHub, and a
 * membership on a host it cannot read is a membership nothing could ever fill in.
 */
export function projectPullRequestRepository(
  identity: { readonly canonicalKey?: string; readonly provider?: string } | null | undefined,
): { readonly host: string; readonly repository: string } | null {
  if (identity?.canonicalKey === undefined || identity.provider !== "github") return null;
  const segments = identity.canonicalKey.split("/").filter((segment) => segment.length > 0);
  if (segments.length !== 3) return null;
  const [host, owner, name] = segments as [string, string, string];
  return { host: host.toLowerCase(), repository: `${owner}/${name}`.toLowerCase() };
}
